/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, apiPublic, post, refreshSession, type ApiResult } from "../api/client";

export type SessionUser = {
  id: string;
  email: string;
  roles: string[];
  firstName: string | null;
  phoneE164: string | null;
};

export type Session =
  | { state: "loading" }
  | { state: "anonymous" }
  | { state: "authenticated"; user: SessionUser };

export type TwoFactorChallenge = {
  token: string;
  phone: string;
  message: string;
};

type LoginBody = {
  requires2FA?: boolean;
  challengeToken?: string;
  phone?: string;
  message?: string;
};

type AuthApi = {
  session: Session;
  login: (email: string, password: string) => Promise<ApiResult<TwoFactorChallenge | null>>;
  loginWithGoogle: (credential: string) => Promise<ApiResult<unknown>>;
  verifyTwoFactor: (challengeToken: string, code: string) => Promise<ApiResult<unknown>>;
  resendTwoFactorCode: (challengeToken: string) => Promise<ApiResult<unknown>>;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
  recheckAccess: () => Promise<boolean>;
};

const AuthContext = createContext<AuthApi | null>(null);

async function fetchMe(): Promise<SessionUser | null> {
  const me = await api<{
    id: string;
    email: string;
    roles?: string[];
    firstName?: string | null;
    phoneE164?: string | null;
  }>("/v1/auth/me");
  if (!me.ok) {
    return null;
  }
  return {
    id: me.value.id,
    email: me.value.email,
    roles: me.value.roles ?? [],
    firstName: me.value.firstName ?? null,
    phoneE164: me.value.phoneE164 ?? null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // The session lives in HttpOnly cookies, so the only way to know whether one
  // exists is to ask: bootstrap with /me and let a 401 mean anonymous.
  const [session, setSession] = useState<Session>({ state: "loading" });

  useEffect(() => {
    if (session.state !== "loading") {
      return;
    }
    let cancelled = false;
    void fetchMe().then((user) => {
      if (cancelled) {
        return;
      }
      setSession(user === null ? { state: "anonymous" } : { state: "authenticated", user });
    });
    return () => {
      cancelled = true;
    };
  }, [session.state]);

  const establishSession = useCallback(async () => {
    const user = await fetchMe();
    setSession(user === null ? { state: "anonymous" } : { state: "authenticated", user });
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<ApiResult<TwoFactorChallenge | null>> => {
      const result = await apiPublic<LoginBody>("/v1/auth/login", post({ email, password }));
      if (!result.ok) {
        return result;
      }
      const { requires2FA, challengeToken, phone, message } = result.value;
      if (requires2FA === true) {
        if (challengeToken === undefined || challengeToken === "") {
          return {
            ok: false,
            error: {
              code: "HP_502",
              message: "The server asked for a verification code but sent no challenge token.",
              fieldErrors: [],
              httpStatus: 502,
            },
          };
        }
        return { ok: true, value: { token: challengeToken, phone: phone ?? "", message: message ?? "" } };
      }
      await establishSession();
      return { ok: true, value: null };
    },
    [establishSession],
  );

  const loginWithGoogle = useCallback(
    async (credential: string) => {
      const result = await apiPublic("/v1/auth/google", post({ credential }));
      if (result.ok) {
        await establishSession();
      }
      return result as ApiResult<unknown>;
    },
    [establishSession],
  );

  const verifyTwoFactor = useCallback(
    async (challengeToken: string, code: string) => {
      const result = await apiPublic("/v1/auth/2fa/verify", post({ challengeToken, totp: code }));
      if (result.ok) {
        await establishSession();
      }
      return result as ApiResult<unknown>;
    },
    [establishSession],
  );

  const resendTwoFactorCode = useCallback(
    (challengeToken: string) =>
      apiPublic("/v1/auth/2fa/request-code", post({ challengeToken })) as Promise<ApiResult<unknown>>,
    [],
  );

  const logout = useCallback(async () => {
    // Clear the session locally first so the redirect to /login is instant and never blocks on the
    // network; the server-side cookie clear is best-effort.
    setSession({ state: "anonymous" });
    await apiPublic("/v1/auth/logout", post({}));
  }, []);

  // The pending-access screen's self-upgrade: /me reads the database, so a fresh grant shows
  // here first; rotating the cookies then mints an access token that carries the new roles.
  const recheckAccess = useCallback(async (): Promise<boolean> => {
    const user = await fetchMe();
    if (user === null || user.roles.length === 0) {
      return false;
    }
    await refreshSession();
    setSession({ state: "authenticated", user });
    return true;
  }, []);

  const hasRole = useCallback(
    (role: string) => session.state === "authenticated" && session.user.roles.includes(role),
    [session],
  );

  const value = useMemo(
    () => ({
      session,
      login,
      loginWithGoogle,
      verifyTwoFactor,
      resendTwoFactorCode,
      logout,
      hasRole,
      recheckAccess,
    }),
    [session, login, loginWithGoogle, verifyTwoFactor, resendTwoFactorCode, logout, hasRole, recheckAccess],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
